import React, { useState, useEffect, useMemo } from "react";
import { Calendar } from "../components/ui/calendar";
import { CheckCircle2, Clock, CalendarDays } from "lucide-react";
import useReveal from "../hooks/useReveal";
import { SERVICES, HAIR_SERVICES, IMAGES } from "../mock";
import { fetchAvailability, createBooking, fetchServices, fetchHairServices } from "../api";
import { useToast } from "../hooks/use-toast";

function toISO(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export default function Booking() {
  useReveal();
  const { toast } = useToast();
  const [allServices, setAllServices] = useState([...SERVICES, ...HAIR_SERVICES]);
  const [date, setDate] = useState(null);
  const [slots, setSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", service: "", time: "", notes: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [emailed, setEmailed] = useState(false);

  useEffect(() => {
    let active = true;
    Promise.all([
      fetchServices().catch(() => SERVICES),
      fetchHairServices().catch(() => HAIR_SERVICES),
    ]).then(([s, h]) => {
      if (active) setAllServices([...(s || []), ...(h || [])]);
    });
    return () => { active = false; };
  }, []);

  useEffect(() => {
    if (!date) return undefined;
    let active = true;
    setLoadingSlots(true);
    setForm((f) => ({ ...f, time: "" }));
    fetchAvailability(toISO(date))
      .then((d) => { if (active) setSlots(d.slots || []); })
      .catch(() => { if (active) setSlots([]); })
      .finally(() => { if (active) setLoadingSlots(false); });
    return () => { active = false; };
  }, [date]);

  const aestheticServices = useMemo(
    () => allServices.filter((s) => !String(s.id).startsWith("hair")),
    [allServices]
  );
  const hairServices = useMemo(
    () => allServices.filter((s) => String(s.id).startsWith("hair")),
    [allServices]
  );

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!date || !form.time || !form.service) {
      toast({ title: "Almost there", description: "Please choose a service, date and time." });
      return;
    }
    setSubmitting(true);
    try {
      const res = await createBooking({ ...form, date: toISO(date) });
      setEmailed(!!(res && res.email_configured));
      setDone(true);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Please try another time.";
      toast({ title: "Could not book", description: msg });
    } finally {
      setSubmitting(false);
    }
  };

  const disabled = (d) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return d < today || d.getDay() === 0; // no past, no Sundays
  };

  return (
    <main className="pt-20">
      <section className="relative py-28 bg-charcoal overflow-hidden">
        <div className="absolute inset-0 opacity-40">
          <img src={IMAGES.interior2} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-charcoal via-charcoal/70 to-charcoal/40" />
        </div>
        <div className="relative z-10 max-w-[1300px] mx-auto px-6 text-center">
          <span className="eyebrow text-gold">Appointments</span>
          <h1 className="font-display text-5xl lg:text-6xl font-light text-cream mt-5 leading-tight">
            Book your <span className="italic text-gold">visit</span>
          </h1>
        </div>
      </section>

      <section className="bg-cream py-20">
        <div className="max-w-[1100px] mx-auto px-6">
          {done ? (
            <div className="bg-white border border-gold/20 rounded-sm p-12 text-center max-w-xl mx-auto reveal is-visible">
              <CheckCircle2 size={54} className="text-gold mx-auto" strokeWidth={1.2} />
              <h2 className="font-display text-4xl text-ink mt-6">Request received</h2>
              <p className="font-sans text-muted-ink mt-4 leading-relaxed">
                Thank you, {form.name.split(" ")[0] || "there"}. We’ve received your request for{" "}
                <span className="text-gold">{form.service}</span> on{" "}
                <span className="text-ink">{toISO(date)}</span> at{" "}
                <span className="text-ink">{form.time}</span>. Our team will call you shortly to confirm.
              </p>
              {emailed && (
                <p className="font-sans text-gold text-sm mt-4">A confirmation email has been sent to {form.email}.</p>
              )}
            </div>
          ) : (
            <div className="grid lg:grid-cols-2 gap-10">
              {/* Calendar + slots */}
              <div className="reveal">
                <div className="bg-white border border-gold/20 rounded-sm p-6">
                  <div className="flex items-center gap-2 mb-4">
                    <CalendarDays size={18} className="text-gold" />
                    <span className="eyebrow text-ink">Select a date</span>
                  </div>
                  <Calendar mode="single" selected={date} onSelect={setDate} disabled={disabled} className="mx-auto" />
                  <p className="text-xs text-muted-ink font-sans mt-2">Mon–Fri 9–5 · Sat 11–3 · Closed Sundays</p>
                </div>

                <div className="bg-white border border-gold/20 rounded-sm p-6 mt-6">
                  <div className="flex items-center gap-2 mb-4">
                    <Clock size={18} className="text-gold" />
                    <span className="eyebrow text-ink">Available times</span>
                  </div>
                  {!date ? (
                    <p className="text-sm text-muted-ink font-sans">Please select a date to see available times.</p>
                  ) : loadingSlots ? (
                    <p className="text-sm text-muted-ink font-sans">Loading…</p>
                  ) : slots.length === 0 ? (
                    <p className="text-sm text-muted-ink font-sans">No times available for this date. Please choose another day.</p>
                  ) : (
                    <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                      {slots.map((s) => (
                        <button
                          key={s}
                          type="button"
                          onClick={() => setForm((f) => ({ ...f, time: s }))}
                          className={`py-2.5 text-sm border transition-colors ${
                            form.time === s ? "bg-charcoal text-cream border-charcoal" : "border-gold/40 text-ink hover:border-gold hover:text-gold"
                          }`}
                        >
                          {s}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Details form */}
              <div className="reveal">
                <form onSubmit={submit} className="bg-white border border-gold/20 rounded-sm p-8">
                  <span className="eyebrow text-gold">Your details</span>
                  <h2 className="font-display text-3xl text-ink mt-2 mb-6">Complete your booking</h2>

                  <label className="eyebrow text-muted-ink block mb-2">Treatment</label>
                  <select name="service" value={form.service} onChange={onChange} required
                    className="w-full bg-cream/50 border border-gold/20 px-4 py-3 text-sm text-ink focus:outline-none focus:border-gold font-sans mb-5">
                    <option value="" disabled>Select a treatment…</option>
                    <optgroup label="Aesthetic Treatments">
                      {aestheticServices.map((s) => (
                        <option key={s.id} value={s.name}>{s.name}</option>
                      ))}
                    </optgroup>
                    <optgroup label="Hair Clinic">
                      {hairServices.map((s) => (
                        <option key={s.id} value={s.name}>{s.name}</option>
                      ))}
                    </optgroup>
                  </select>

                  <div className="grid sm:grid-cols-2 gap-5">
                    <div>
                      <label className="eyebrow text-muted-ink block mb-2">Full Name</label>
                      <input name="name" value={form.name} onChange={onChange} required
                        className="w-full bg-cream/50 border border-gold/20 px-4 py-3 text-sm text-ink focus:outline-none focus:border-gold font-sans" />
                    </div>
                    <div>
                      <label className="eyebrow text-muted-ink block mb-2">Phone</label>
                      <input name="phone" value={form.phone} onChange={onChange} required
                        className="w-full bg-cream/50 border border-gold/20 px-4 py-3 text-sm text-ink focus:outline-none focus:border-gold font-sans" />
                    </div>
                  </div>
                  <div className="mt-5">
                    <label className="eyebrow text-muted-ink block mb-2">Email</label>
                    <input type="email" name="email" value={form.email} onChange={onChange} required
                      className="w-full bg-cream/50 border border-gold/20 px-4 py-3 text-sm text-ink focus:outline-none focus:border-gold font-sans" />
                  </div>
                  <div className="mt-5">
                    <label className="eyebrow text-muted-ink block mb-2">Notes (optional)</label>
                    <textarea name="notes" rows={3} value={form.notes} onChange={onChange}
                      className="w-full bg-cream/50 border border-gold/20 px-4 py-3 text-sm text-ink focus:outline-none focus:border-gold font-sans resize-none" />
                  </div>

                  <div className="mt-6 p-4 bg-cream-deep rounded-sm text-sm font-sans text-muted-ink">
                    {date && form.time ? (
                      <span>Booking for <span className="text-ink">{toISO(date)}</span> at <span className="text-ink">{form.time}</span></span>
                    ) : (
                      <span>Select a date and time on the left.</span>
                    )}
                  </div>

                  <button type="submit" disabled={submitting} className="btn-gold w-full mt-6 disabled:opacity-50">
                    {submitting ? "Booking…" : "Confirm Booking"}
                  </button>
                </form>
              </div>
            </div>
          )}
        </div>
      </section>
    </main>
  );
}
